<template>
  <section class="tplan-panel">
    <div class="tplan-panel__head">
      <div>
        <h2 class="tplan-panel__title">Terapie</h2>
        <p class="tplan-panel__subtitle">
          Schede terapia strutturate per questo ospite
        </p>
      </div>
    </div>

    <div v-if="loading" class="tplan-state">Caricamento terapie…</div>
    <div v-else-if="error" class="tplan-state">{{ error }}</div>

    <template v-else>
      <!-- Terapia corrente -->
      <div class="tplan-section">
        <h3 class="tplan-section__title">Terapia corrente</h3>

        <div v-if="currentPlan" class="tplan-current">
          <div class="tplan-current__head">
            <div>
              <div class="tplan-name">
                {{ currentPlan.title }}
                <span class="tplan-badge tplan-badge--current">Corrente</span>
              </div>
              <div class="tplan-meta">
                Efficacia {{ formatDate(currentPlan.effectiveDate) }} ·
                {{ currentPlan.linesCount }} righe
                <template v-if="currentPlan.prescribedBy">
                  · {{ currentPlan.prescribedBy }}
                </template>
              </div>
            </div>
            <div class="tplan-actions">
              <button
                class="villa-btn villa-btn--ghost"
                @click="openDetail(currentPlan)"
              >
                <i class="fa-solid fa-list-ul"></i> Dettaglio
              </button>
              <button
                class="villa-btn villa-btn--ghost"
                @click="openEditPlan(currentPlan)"
              >
                <i class="fa-solid fa-pen-to-square"></i> Modifica
              </button>
              <button
                class="villa-btn villa-btn--danger"
                @click="askArchivePlan(currentPlan)"
              >
                <i class="fa-solid fa-box-archive"></i> Archivia
              </button>
            </div>
          </div>
        </div>

        <div v-else class="tplan-state">
          Nessuna terapia corrente.
          <button
            class="villa-btn villa-btn--primary"
            style="margin-left: 8px"
            @click="openCreatePlan"
          >
            <i class="fa-solid fa-plus"></i> Nuova terapia
          </button>
        </div>
      </div>

      <!-- Archivio terapie -->
      <div class="tplan-section">
        <h3 class="tplan-section__title">Archivio terapie</h3>
        <div v-if="archivedPlans.length === 0" class="tplan-state">
          Nessuna terapia archiviata.
        </div>
        <div v-else class="tplan-table-wrap">
          <table class="guests-table">
            <thead>
              <tr>
                <th>Titolo</th>
                <th>Efficacia</th>
                <th>Righe</th>
                <th>Archiviata il</th>
                <th class="actions-cell">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in archivedPlans" :key="p.id">
                <td>{{ p.title }}</td>
                <td>{{ formatDate(p.effectiveDate) }}</td>
                <td>{{ p.linesCount }}</td>
                <td>{{ formatDateTime(p.archivedAt) }}</td>
                <td class="actions-cell">
                  <button
                    class="icon-btn"
                    title="Dettaglio (sola lettura)"
                    @click="openDetail(p)"
                  >
                    <i class="fa-solid fa-list-ul"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- Modale scheda terapia (crea/modifica) -->
    <VueFinalModal v-model="planModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">
        {{ planModal.mode === "edit" ? "Modifica terapia" : "Nuova terapia" }}
      </h3>
      <div class="form-grid">
        <div class="form-field form-field--full">
          <label>Titolo</label>
          <input
            v-model.trim="planForm.title"
            type="text"
            placeholder="Terapia corrente"
          />
        </div>
        <div class="form-field">
          <label>Data efficacia</label>
          <input v-model="planForm.effectiveDate" type="date" />
        </div>
        <div class="form-field">
          <label>Prescrittore</label>
          <input v-model.trim="planForm.prescribedBy" type="text" />
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="planForm.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="planModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="planModal.busy"
          @click="submitPlan"
        >
          {{
            planModal.busy
              ? "Salvataggio…"
              : planModal.mode === "edit"
              ? "Salva"
              : "Crea"
          }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Modale dettaglio scheda + righe -->
    <VueFinalModal
      v-model="detail.open"
      content-class="villa-modal villa-modal--wide"
    >
      <h3 class="villa-modal__title">
        {{ detail.plan ? detail.plan.title : "Dettaglio terapia" }}
        <span
          v-if="detail.plan"
          :class="['tplan-badge', 'tplan-badge--' + detail.plan.status]"
        >
          {{ statusLabel(detail.plan.status) }}
        </span>
      </h3>

      <div v-if="detail.loading" class="tplan-state">Caricamento…</div>
      <template v-else-if="detail.plan">
        <p class="tplan-detail__meta">
          Efficacia {{ formatDate(detail.plan.effectiveDate) }}
          <template v-if="detail.plan.prescribedBy">
            · Prescrittore: {{ detail.plan.prescribedBy }}
          </template>
          <template v-if="detail.plan.notes">
            · {{ detail.plan.notes }}</template
          >
        </p>

        <div class="tplan-detail__lineshead">
          <strong>Righe terapia</strong>
          <button
            v-if="detail.plan.editable"
            class="villa-btn villa-btn--primary"
            @click="openCreateLine"
          >
            <i class="fa-solid fa-plus"></i> Aggiungi riga
          </button>
        </div>

        <div v-if="detail.plan.lines.length === 0" class="tplan-state">
          Nessuna riga.
          {{ detail.plan.editable ? "Aggiungi la prima riga." : "" }}
        </div>
        <div v-else class="tplan-table-wrap">
          <table class="guests-table">
            <thead>
              <tr>
                <th>Orario</th>
                <th>Farmaco</th>
                <th>Dose</th>
                <th>Via</th>
                <th>Indicazione</th>
                <th>Note</th>
                <th v-if="detail.plan.editable" class="actions-cell">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ln in detail.plan.lines" :key="ln.id">
                <td class="tline-time">{{ ln.scheduledTime }}</td>
                <td>
                  <div class="tline-med">{{ ln.medicationNameSnapshot }}</div>
                  <div v-if="ln.strengthSnapshot" class="tline-sub">
                    {{ ln.strengthSnapshot }}
                  </div>
                </td>
                <td>{{ fmtNum(ln.doseAmount) }} {{ ln.doseUnitSnapshot }}</td>
                <td>{{ routeLabel(ln.administrationRoute) }}</td>
                <td>{{ ln.timingNotes || "—" }}</td>
                <td>{{ ln.notes || "—" }}</td>
                <td v-if="detail.plan.editable" class="actions-cell">
                  <button
                    class="icon-btn"
                    title="Modifica"
                    @click="openEditLine(ln)"
                  >
                    <i class="fa-solid fa-pen-to-square"></i>
                  </button>
                  <button
                    class="icon-btn icon-btn--danger"
                    title="Archivia riga"
                    @click="askArchiveLine(ln)"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <div class="form-actions">
        <button class="villa-btn villa-btn--ghost" @click="detail.open = false">
          Chiudi
        </button>
        <button
          v-if="lastPdf"
          class="villa-btn villa-btn--ghost"
          :disabled="downloadingPdf"
          @click="downloadLastPdf"
        >
          <i class="fa-solid fa-download"></i> Scarica PDF
        </button>
        <button
          v-if="detail.plan"
          class="villa-btn villa-btn--primary"
          :disabled="exportingPdf"
          @click="exportPdf"
        >
          <i class="fa-solid fa-file-pdf"></i>
          {{ exportingPdf ? "Generazione…" : "Esporta PDF" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Modale riga terapia (crea/modifica) -->
    <VueFinalModal v-model="lineModal.open" content-class="villa-modal">
      <h3 class="villa-modal__title">
        {{ lineModal.mode === "edit" ? "Modifica riga" : "Nuova riga terapia" }}
      </h3>
      <div class="form-grid">
        <div class="form-field form-field--full">
          <label>Farmaco personale <span class="required">*</span></label>
          <select v-model="lineForm.guestMedicationItemId">
            <option :value="null" disabled>— seleziona farmaco —</option>
            <option v-for="m in medicationItems" :key="m.id" :value="m.id">
              {{ m.name
              }}<template v-if="m.strength"> ({{ m.strength }})</template>
            </option>
          </select>
          <span v-if="medicationItems.length === 0" class="tline-sub">
            Nessun farmaco personale. Aggiungine uno nella sezione "Farmaci
            personali".
          </span>
        </div>

        <div v-if="selectedMedItem" class="med-pick-info">
          <div>
            <strong>{{ selectedMedItem.name }}</strong>
            <template v-if="selectedMedItem.strength">
              · {{ selectedMedItem.strength }}</template
            >
            <template v-if="selectedMedItem.pharmaceuticalForm">
              · {{ selectedMedItem.pharmaceuticalForm }}</template
            >
          </div>
          <div>
            Unità dose: <strong>{{ selectedDoseUnit }}</strong> · Disponibilità:
            <span
              :class="{
                'med-pick-info__warn':
                  Number(selectedMedItem.availableDoses) <= 0,
              }"
            >
              {{ fmtNum(selectedMedItem.availableDoses) }}
              {{ selectedDoseUnit }}
            </span>
            <template v-if="Number(selectedMedItem.availableDoses) <= 0">
              — scorta non caricata (la terapia può comunque essere prescritta)
            </template>
          </div>
        </div>

        <div class="form-field">
          <label>Orario <span class="required">*</span></label>
          <input v-model="lineForm.scheduledTime" type="time" />
        </div>
        <div class="form-field">
          <label>Numero unità <span class="required">*</span></label>
          <input
            v-model="lineForm.doseAmount"
            type="number"
            min="0"
            step="any"
          />
        </div>
        <div class="form-field">
          <label>Unità dose</label>
          <input :value="selectedDoseUnit" type="text" readonly />
        </div>
        <div class="form-field">
          <label>Via di somministrazione</label>
          <select v-model="lineForm.administrationRoute">
            <option v-for="r in routes" :key="r.value" :value="r.value">
              {{ r.label }}
            </option>
          </select>
        </div>
        <div class="form-field form-field--full">
          <label>Indicazione / momento</label>
          <input
            v-model.trim="lineForm.timingNotes"
            type="text"
            placeholder="es. dopo colazione"
          />
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="lineForm.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="villa-btn villa-btn--ghost"
          @click="lineModal.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="lineModal.busy"
          @click="submitLine"
        >
          {{
            lineModal.busy
              ? "Salvataggio…"
              : lineModal.mode === "edit"
              ? "Salva"
              : "Aggiungi"
          }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma archiviazione (terapia o riga) -->
    <VueFinalModal v-model="confirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">
        Archivia {{ confirm.kind === "plan" ? "terapia" : "riga" }}
      </h3>
      <p class="villa-confirm__text">
        Vuoi archiviare <strong>{{ confirm.label }}</strong
        >?
        <template v-if="confirm.kind === 'plan'">
          La terapia diventerà di sola lettura e finirà nell'archivio.
        </template>
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="confirm.busy"
          @click="confirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="confirm.busy"
          @click="confirmArchive"
        >
          {{ confirm.busy ? "Archivia…" : "Archivia" }}
        </button>
      </div>
    </VueFinalModal>
  </section>
</template>

<script>
import { mapActions } from "vuex";

const ROUTES = [
  { value: "oral", label: "Orale" },
  { value: "sublingual", label: "Sublinguale" },
  { value: "topical", label: "Topica" },
  { value: "inhalation", label: "Inalatoria" },
  { value: "injection", label: "Iniettiva" },
  { value: "ophthalmic", label: "Oftalmica" },
  { value: "otic", label: "Otologica" },
  { value: "rectal", label: "Rettale" },
  { value: "other", label: "Altro" },
];
const STATUSES = { current: "Corrente", archived: "Archiviata" };

const emptyPlanForm = () => ({
  title: "Terapia corrente",
  effectiveDate: "",
  prescribedBy: "",
  notes: "",
});
const emptyLineForm = () => ({
  guestMedicationItemId: null,
  scheduledTime: "",
  doseAmount: "1",
  administrationRoute: "oral",
  timingNotes: "",
  notes: "",
});

export default {
  name: "GuestTherapyPlanPanel",
  props: {
    guestId: { type: [String, Number], required: true },
  },
  emits: ["documents-changed"],
  data() {
    return {
      plans: [],
      medicationItems: [],
      loading: true,
      error: "",
      routes: ROUTES,
      planForm: emptyPlanForm(),
      planModal: { open: false, mode: "create", busy: false, id: null },
      detail: { open: false, loading: false, plan: null },
      lineForm: emptyLineForm(),
      lineModal: { open: false, mode: "create", busy: false, id: null },
      confirm: { open: false, kind: "plan", busy: false, id: null, label: "" },
      exportingPdf: false,
      downloadingPdf: false,
      lastPdf: null,
    };
  },
  computed: {
    currentPlan() {
      return this.plans.find((p) => p.status === "current") || null;
    },
    archivedPlans() {
      return this.plans.filter((p) => p.status === "archived");
    },
    selectedMedItem() {
      const id = Number(this.lineForm.guestMedicationItemId);
      return this.medicationItems.find((m) => m.id === id) || null;
    },
    selectedDoseUnit() {
      return this.selectedMedItem ? this.selectedMedItem.doseUnit : "unità";
    },
  },
  methods: {
    ...mapActions([
      "fetchGuestTherapyPlans",
      "fetchGuestTherapyPlan",
      "createGuestTherapyPlan",
      "updateGuestTherapyPlan",
      "archiveGuestTherapyPlan",
      "createGuestTherapyPlanLine",
      "updateGuestTherapyPlanLine",
      "archiveGuestTherapyPlanLine",
      "fetchGuestMedicationItems",
      "generateGuestTherapyPlanPdf",
      "downloadGuestDocument",
    ]),

    // --- Export PDF (D5) ---
    async exportPdf() {
      if (!this.detail.plan) return;
      this.exportingPdf = true;
      try {
        const result = await this.generateGuestTherapyPlanPdf({
          guestId: this.guestId,
          planId: this.detail.plan.id,
        });
        // Lettura robusta: la action ritorna il document, ma accettiamo anche
        // forme { document } o { data: { document } } senza andare in errore.
        const createdDoc =
          result?.document || result?.data?.document || result || null;
        this.lastPdf = createdDoc;
        this.$toast.success(
          "PDF terapia generato e salvato nella Cartella documentale."
        );
        // Segnala alla pagina di ricaricare la Cartella documentale.
        this.$emit("documents-changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Generazione PDF non riuscita."
        );
      } finally {
        this.exportingPdf = false;
      }
    },
    async downloadLastPdf() {
      if (!this.lastPdf) return;
      this.downloadingPdf = true;
      let url = null;
      try {
        const response = await this.downloadGuestDocument({
          guestId: this.guestId,
          documentId: this.lastPdf.id,
        });
        const blob = new Blob([response.data], {
          type: response.data?.type || "application/pdf",
        });
        url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          this.lastPdf.originalFilename ||
          this.lastPdf.storedFilename ||
          `terapia-${this.lastPdf.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Download PDF non riuscito."
        );
      } finally {
        if (url) window.URL.revokeObjectURL(url);
        this.downloadingPdf = false;
      }
    },

    statusLabel(s) {
      return STATUSES[s] || s;
    },
    routeLabel(v) {
      const f = ROUTES.find((r) => r.value === v);
      return f ? f.label : v;
    },
    fmtNum(n) {
      const v = Number(n);
      return Number.isFinite(v) ? String(v) : "0";
    },
    formatDate(d) {
      if (!d) return "—";
      const [y, m, day] = String(d).slice(0, 10).split("-");
      return day && m && y ? `${day}/${m}/${y}` : d;
    },
    formatDateTime(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return "—";
      return `${this.formatDate(dt.toISOString())} ${String(
        dt.getHours()
      ).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
    },

    async load() {
      this.loading = true;
      this.error = "";
      try {
        const [plans, meds] = await Promise.all([
          this.fetchGuestTherapyPlans(this.guestId),
          this.fetchGuestMedicationItems(this.guestId),
        ]);
        this.plans = plans;
        this.medicationItems = meds;
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Errore nel caricamento delle terapie.";
      } finally {
        this.loading = false;
      }
    },
    async refreshMeds() {
      try {
        this.medicationItems = await this.fetchGuestMedicationItems(
          this.guestId
        );
      } catch {
        // non bloccante per il form riga
      }
    },

    // --- Scheda ---
    openCreatePlan() {
      this.planForm = emptyPlanForm();
      this.planModal = { open: true, mode: "create", busy: false, id: null };
    },
    openEditPlan(plan) {
      this.planForm = {
        title: plan.title || "",
        effectiveDate: plan.effectiveDate
          ? String(plan.effectiveDate).slice(0, 10)
          : "",
        prescribedBy: plan.prescribedBy || "",
        notes: plan.notes || "",
      };
      this.planModal = { open: true, mode: "edit", busy: false, id: plan.id };
    },
    buildPlanPayload() {
      return {
        title: this.planForm.title || "Terapia corrente",
        effectiveDate: this.planForm.effectiveDate || null,
        prescribedBy: this.planForm.prescribedBy || null,
        notes: this.planForm.notes || null,
      };
    },
    async submitPlan() {
      this.planModal.busy = true;
      try {
        const payload = this.buildPlanPayload();
        if (this.planModal.mode === "edit") {
          await this.updateGuestTherapyPlan({
            guestId: this.guestId,
            planId: this.planModal.id,
            payload,
          });
          this.$toast.success("Terapia aggiornata.");
        } else {
          await this.createGuestTherapyPlan({
            guestId: this.guestId,
            payload,
          });
          this.$toast.success("Terapia creata.");
        }
        this.planModal.open = false;
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Salvataggio terapia non riuscito."
        );
      } finally {
        this.planModal.busy = false;
      }
    },
    askArchivePlan(plan) {
      this.confirm = {
        open: true,
        kind: "plan",
        busy: false,
        id: plan.id,
        label: plan.title,
      };
    },

    // --- Dettaglio ---
    async openDetail(plan) {
      this.lastPdf = null;
      this.detail = { open: true, loading: true, plan: null };
      await this.refreshDetail(plan.id);
    },
    async refreshDetail(planId) {
      this.detail.loading = true;
      try {
        this.detail.plan = await this.fetchGuestTherapyPlan({
          guestId: this.guestId,
          planId,
        });
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Errore nel caricamento del dettaglio."
        );
        this.detail.open = false;
      } finally {
        this.detail.loading = false;
      }
    },

    // --- Righe ---
    openCreateLine() {
      this.lineForm = emptyLineForm();
      this.lineModal = { open: true, mode: "create", busy: false, id: null };
      this.refreshMeds();
    },
    openEditLine(ln) {
      this.lineForm = {
        guestMedicationItemId: ln.guestMedicationItemId,
        scheduledTime: ln.scheduledTime || "",
        doseAmount: ln.doseAmount != null ? String(ln.doseAmount) : "1",
        administrationRoute: ln.administrationRoute || "oral",
        timingNotes: ln.timingNotes || "",
        notes: ln.notes || "",
      };
      this.lineModal = { open: true, mode: "edit", busy: false, id: ln.id };
      this.refreshMeds();
    },
    buildLinePayload() {
      return {
        guestMedicationItemId: Number(this.lineForm.guestMedicationItemId),
        scheduledTime: this.lineForm.scheduledTime,
        doseAmount: Number(this.lineForm.doseAmount),
        administrationRoute: this.lineForm.administrationRoute,
        timingNotes: this.lineForm.timingNotes || null,
        notes: this.lineForm.notes || null,
      };
    },
    async submitLine() {
      if (!this.lineForm.guestMedicationItemId) {
        return this.$toast.error("Seleziona un farmaco.");
      }
      if (!this.lineForm.scheduledTime) {
        return this.$toast.error("L'orario è obbligatorio.");
      }
      const dose = Number(this.lineForm.doseAmount);
      if (!Number.isFinite(dose) || dose <= 0) {
        return this.$toast.error(
          "Il numero di unità deve essere maggiore di 0."
        );
      }
      this.lineModal.busy = true;
      try {
        const payload = this.buildLinePayload();
        if (this.lineModal.mode === "edit") {
          await this.updateGuestTherapyPlanLine({
            guestId: this.guestId,
            planId: this.detail.plan.id,
            lineId: this.lineModal.id,
            payload,
          });
          this.$toast.success("Riga aggiornata.");
        } else {
          await this.createGuestTherapyPlanLine({
            guestId: this.guestId,
            planId: this.detail.plan.id,
            payload,
          });
          this.$toast.success("Riga aggiunta.");
        }
        this.lineModal.open = false;
        await this.refreshDetail(this.detail.plan.id);
        await this.load();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Salvataggio riga non riuscito."
        );
      } finally {
        this.lineModal.busy = false;
      }
    },
    askArchiveLine(ln) {
      this.confirm = {
        open: true,
        kind: "line",
        busy: false,
        id: ln.id,
        label: `${ln.medicationNameSnapshot} ${ln.scheduledTime}`,
      };
    },

    // --- Archiviazione condivisa ---
    async confirmArchive() {
      this.confirm.busy = true;
      try {
        if (this.confirm.kind === "plan") {
          await this.archiveGuestTherapyPlan({
            guestId: this.guestId,
            planId: this.confirm.id,
          });
          this.$toast.success("Terapia archiviata.");
          this.confirm.open = false;
          if (this.detail.open) this.detail.open = false;
          await this.load();
        } else {
          await this.archiveGuestTherapyPlanLine({
            guestId: this.guestId,
            planId: this.detail.plan.id,
            lineId: this.confirm.id,
          });
          this.$toast.success("Riga archiviata.");
          this.confirm.open = false;
          await this.refreshDetail(this.detail.plan.id);
          await this.load();
        }
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Archiviazione non riuscita."
        );
      } finally {
        this.confirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/GuestTherapyPlanPanel.css" />
